import React from 'react';
import { Button, Tooltip } from '@shared/components';
import type { StepPosition } from '../utils/stepPosition';
import type { PaymentNavControl } from '../utils/paymentNavControl';

const AGREEMENT_MISSING_REASON = 'This sale is completed. Its agreement has not been created yet.';

interface QuoteProgressNavProps {
    stepPosition: StepPosition | null;
    currentStep: string;
    status?: string;
    canGoBack: boolean;
    canProceed: boolean;
    onPrev: () => void;
    onNext: () => void;
    onRequestStartOver: () => void;
    onNavigateToAgreementsList: () => void;
    onNavigateToAgreement: () => void;
    /** Non-null exactly when the branch's primary control is disabled for a validity reason;
     *  `completingSale` also disables it but is not a blocked precondition, so it reports none. */
    blockReason?: string | null;
    /** Disables the summary's payment control; it never changes which control is shown. */
    canInitiateCheckout: boolean;
    initiatingCheckout: boolean;
    /** Which payment control the summary shows, resolved once by `QuoteWizard`. */
    paymentControl: PaymentNavControl;
    onInitiateCheckout: () => void;
    onRetryCheckout?: () => void;
    /** True once the confirmation has established the row is visible with no agreement yet. */
    agreementConfirmationMissing?: boolean;
}

interface BlockedReasonProps {
    reason: string | null | undefined;
    reasonId: string;
    children: React.ReactNode;
}

/** The reason a control is disabled lives in its tooltip only; the id is the sr-only copy
 *  the control references through `aria-describedby`. */
const BlockedReason: React.FC<BlockedReasonProps> = ({ reason, reasonId, children }) =>
    reason ? (
        <Tooltip content={reason} id={reasonId}>
            {children}
        </Tooltip>
    ) : (
        <>{children}</>
    );

/** Reason-blocked controls stay focusable (aria-disabled) so the tooltip also opens on focus. */
const blockedControlProps = (reason: string | null | undefined, reasonId: string, blocked: boolean) =>
    reason ? { ariaDisabled: blocked, ariaDescribedBy: reasonId } : { disabled: blocked };

/**
 * Progress bar + wizard navigation for the quote journey. Presentational: QuoteWizard owns state
 * and handlers so this can render at the top and bottom of the panel from one prop object.
 */
export const QuoteProgressNav: React.FC<QuoteProgressNavProps> = ({
    stepPosition,
    currentStep,
    status,
    canGoBack,
    canProceed,
    onPrev,
    onNext,
    onRequestStartOver,
    onNavigateToAgreementsList,
    onNavigateToAgreement,
    blockReason,
    canInitiateCheckout,
    initiatingCheckout,
    paymentControl,
    onInitiateCheckout,
    onRetryCheckout,
    agreementConfirmationMissing = false,
}) => {
    // Rendered twice from one prop object, so each instance needs its own describedby targets.
    const uniqueId = React.useId();
    const reasonId = `${uniqueId}-block-reason`;
    const agreementReasonId = `${uniqueId}-agreement-reason`;
    const agreementReason = agreementConfirmationMissing ? AGREEMENT_MISSING_REASON : null;

    return (
        <div>
            <div>
                <div className="flex justify-between text-sm text-mp-muted mb-2">
                    <span>Progress</span>
                    {stepPosition && <span>{stepPosition.label}</span>}
                </div>
                <div className="w-full bg-mp-border rounded-full h-2">
                    <div
                        data-testid="quote-progress-fill"
                        className="bg-mp-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${stepPosition?.widthPercent ?? 0}%` }}
                    />
                </div>
            </div>

            <div className="flex justify-between mt-4">
                <Button variant="outline" onClick={onPrev} disabled={!canGoBack} size="sm">
                    Previous
                </Button>

                <div className="flex space-x-3">
                    <Button variant="outline" onClick={onRequestStartOver} size="sm">
                        Start Over
                    </Button>

                    {currentStep !== 'summary' ? (
                        <BlockedReason reason={blockReason} reasonId={reasonId}>
                            <Button
                                onClick={onNext}
                                size="sm"
                                {...blockedControlProps(blockReason, reasonId, !canProceed)}
                            >
                                Next Step
                            </Button>
                        </BlockedReason>
                    ) : status === 'completed' ? (
                        <>
                            <Button variant="outline" onClick={onNavigateToAgreementsList} size="sm">
                                Go to Agreements
                            </Button>
                            <BlockedReason reason={agreementReason} reasonId={agreementReasonId}>
                                <Button
                                    onClick={onNavigateToAgreement}
                                    size="sm"
                                    {...blockedControlProps(agreementReason, agreementReasonId, agreementConfirmationMissing)}
                                >
                                    Load Agreement
                                </Button>
                            </BlockedReason>
                        </>
                    ) : paymentControl === 'hidden' ? null : paymentControl === 'retry' ? (
                        <BlockedReason reason={blockReason} reasonId={reasonId}>
                            <Button
                                onClick={onRetryCheckout}
                                size="sm"
                                {...blockedControlProps(blockReason, reasonId, !canInitiateCheckout)}
                            >
                                {initiatingCheckout ? 'Starting payment…' : 'Retry payment'}
                            </Button>
                        </BlockedReason>
                    ) : (
                        <BlockedReason reason={blockReason} reasonId={reasonId}>
                            <Button
                                onClick={onInitiateCheckout}
                                size="sm"
                                {...blockedControlProps(blockReason, reasonId, !canInitiateCheckout)}
                            >
                                {initiatingCheckout
                                    ? 'Starting payment…'
                                    : paymentControl === 'reopen'
                                        ? 'Reopen payment'
                                        : 'Continue to payment'}
                            </Button>
                        </BlockedReason>
                    )}
                </div>
            </div>
        </div>
    );
};
